import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Dumbbell,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  CalendarDays,
  TrendingUp,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  QrCode,
  Calendar as CalendarIcon,
  Play,
  Square,
  ExternalLink,
  MinusCircle,
  Shuffle,
  Plus,
  Zap,
  ClipboardList,
  Trash2,
  Settings,
} from "lucide-react";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { cn } from "../../lib/utils";
import { Html5Qrcode } from "html5-qrcode";
import api from "../../lib/api";
import { toast } from "sonner";
import DayDetailInline from "../../components/workouts/DayDetailInline";
import WorkoutCalendar from "../../components/workouts/WorkoutCalendar";
import WorkoutSessionView from "../../components/workouts/WorkoutSessionView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_LABEL = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const CAL_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const STATUS_CLS = {
  completed: {
    text: "text-green-500",
    bg: "bg-green-500",
    light: "bg-green-500/10",
    icon: CheckCircle2,
    label: "Done",
  },
  missed: {
    text: "text-red-500",
    bg: "bg-red-500",
    light: "bg-red-500/10",
    icon: XCircle,
    label: "Missed",
  },
  upcoming: {
    text: "text-amber-400",
    bg: "bg-amber-400",
    light: "bg-amber-400/10",
    icon: Clock,
    label: "Upcoming",
  },
  skipped: {
    text: "text-muted-foreground/60",
    bg: "bg-muted-foreground/30",
    light: "bg-muted-foreground/10",
    icon: MinusCircle,
    label: "Skipped",
  },
};

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toYMD(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
function formatGraceDate(dateStr) {
  if (!dateStr) return "";
  const d = parseLocalDate(dateStr);
  return d ? format(d, "d MMM") : "";
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
              {label}
            </p>
            <p className={`text-2xl font-black font-heading ${color}`}>
              {value}
            </p>
          </div>
          <div
            className={`w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 ${color}`}
          >
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── DayDetail bottom-sheet ────────────────────────────────────────────────────
function OldDayDetailInline({
  session,
  allowMemberToComplete,
  onSuccess,
  hideHeader = false,
  enableTimer = false,
}) {
  const [loading, setLoading] = useState(false);
  const [localSession, setLocalSession] = useState(session);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTimer, setActiveTimer] = useState(null); // { secIdx, wIdx, setIdx }
  const [elapsed, setElapsed] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, description, onConfirm }
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    src: "",
    name: "",
  });

  const isImage = (file) => {
    if (file.type && file.type.startsWith("image/")) return true;
    const url = file.url || "";
    return /\.(jpg|jpeg|png|webp|gif|svg)/i.test(url);
  };

  useEffect(() => {
    if (!activeTimer) {
      setElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatMeaningfulTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins === 0) {
      return `${secs} sec`;
    }
    if (secs === 0) {
      return `${mins} mins`;
    }
    return `${mins} mins ${secs} sec`;
  };

  const handleStartTimer = (secIdx, wIdx, setIdx) => {
    setActiveTimer({ secIdx, wIdx, setIdx });
    setElapsed(0);
  };

  const handleStopTimerAndComplete = (secIdx, wIdx, setIdx) => {
    const sessionCopy = localSession.workoutSession || { sections: [] };
    const section = sessionCopy.sections?.[secIdx];
    const workout = section?.workouts?.[wIdx];
    const workoutDetails = getWorkoutDetails(workout?.workoutId);
    const workoutTitle = workoutDetails?.title || "Exercise";

    const formatted = formatMeaningfulTime(elapsed);
    setConfirmDialog({
      title: "Set Completed! 🎉",
      type: "timer",
      workoutTitle,
      formattedTime: formatted,
      onConfirm: () => {
        setActiveTimer(null);
        setElapsed(0);
        handleToggleSetComplete(secIdx, wIdx, setIdx, true, true);
      },
    });
  };

  useEffect(() => {
    setLocalSession(session);
  }, [session]);

  const sectionsToRender = useMemo(() => {
    if (!localSession) return [];
    const rawSections = localSession.workoutSession?.sections;
    if (rawSections && rawSections.length > 0) {
      return rawSections;
    }
    return [
      {
        name: "Workouts",
        workouts: (localSession.workoutIds || []).map((id) => ({
          workoutId: id,
        })),
      },
    ];
  }, [localSession]);

  const hasWorkouts = useMemo(() => {
    if (!sectionsToRender || sectionsToRender.length === 0) return false;
    return sectionsToRender.some(
      (sec) => sec.workouts && sec.workouts.length > 0,
    );
  }, [sectionsToRender]);

  if (!localSession) return null;

  const st = STATUS_CLS[localSession.status] || STATUS_CLS.upcoming;
  const Icon = st.icon;
  const dateLabel = localSession.date
    ? format(parseLocalDate(localSession.date), "EEEE, MMM d, yyyy")
    : "";

  const getWorkoutDetails = (workoutId) => {
    return localSession.workouts?.find((w) => w.id === workoutId);
  };

  const getLoggedSetDetails = (workout, workoutDetails) => {
    if (workout.loggedSetDetails && workout.loggedSetDetails.length > 0) {
      return workout.loggedSetDetails;
    }
    const suggestions = workoutDetails?.setDetails || workout.setDetails || [];
    return suggestions.map((set) => ({
      ...set,
      completed: false,
    }));
  };

  const handleUpdateWorkoutSession = async (updatedSession) => {
    const updated = {
      ...localSession,
      workoutSession: updatedSession,
    };
    setLocalSession(updated);

    const token = localStorage.getItem("member_token");
    if (!token) return;

    try {
      await api.put(
        `/member-portal/workouts/schedule/${localSession.id}/session`,
        { workoutSession: updatedSession },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // 2. Recalculate automatic status changes
      let allSetsCompleted = true;
      let totalSetsCount = 0;

      updatedSession.sections?.forEach((sec) => {
        sec.workouts?.forEach((w) => {
          const workoutDetails = localSession.workouts?.find(
            (dw) => dw.id === w.workoutId,
          );
          const loggedSets =
            w.loggedSetDetails || getLoggedSetDetails(w, workoutDetails);
          loggedSets.forEach((set) => {
            totalSetsCount++;
            if (!set.completed) {
              allSetsCompleted = false;
            }
          });
        });
      });

      if (totalSetsCount === 0) {
        let allWorkoutsCompleted = true;
        let totalWorkoutsCount = 0;
        updatedSession.sections?.forEach((sec) => {
          sec.workouts?.forEach((w) => {
            totalWorkoutsCount++;
            if (!w.completed) {
              allWorkoutsCompleted = false;
            }
          });
        });
        if (totalWorkoutsCount > 0) {
          allSetsCompleted = allWorkoutsCompleted;
        } else {
          allSetsCompleted = false;
        }
      }

      const currentDayStatus = localSession.status;
      if (allSetsCompleted && currentDayStatus !== "completed") {
        setConfirmDialog({
          title: "Complete Workout Day? 🎉",
          type: "session-auto",
          action: "complete",
          description:
            'Fantastic! You have completed all exercises in this workout session. 🎉\n\nWould you like to mark this entire day as "Completed" on your calendar?',
          onConfirm: async () => {
            try {
              await api.put(
                `/member-portal/workouts/schedule/${localSession.id}/status`,
                { status: "completed" },
                { headers: { Authorization: `Bearer ${token}` } },
              );
              toast.success("Day marked as completed!");
              onSuccess?.();
            } catch (err) {
              toast.error("Failed to update status");
            }
          },
        });
      } else if (!allSetsCompleted && currentDayStatus === "completed") {
        setConfirmDialog({
          title: "Revert Day Status?",
          type: "session-auto",
          action: "reset",
          description:
            'Some exercises are unchecked. Would you like to revert this day\'s status to "Upcoming" on your calendar?',
          onConfirm: async () => {
            try {
              await api.put(
                `/member-portal/workouts/schedule/${localSession.id}/status`,
                { status: "upcoming" },
                { headers: { Authorization: `Bearer ${token}` } },
              );
              toast.success("Day marked as upcoming");
              onSuccess?.();
            } catch (err) {
              toast.error("Failed to update status");
            }
          },
        });
      } else {
        onSuccess?.();
      }
    } catch (err) {
      console.error("Failed to save session updates:", err);
      toast.error("Failed to auto-save workout log");
    }
  };

  const handleToggleSetComplete = (
    sectionIdx,
    workoutIdx,
    setIdx,
    completedVal,
    skipConfirm = false,
  ) => {
    if (!localSession) return;

    // Stop active timer if this set is being toggled
    if (
      activeTimer &&
      activeTimer.secIdx === sectionIdx &&
      activeTimer.wIdx === workoutIdx &&
      activeTimer.setIdx === setIdx
    ) {
      setActiveTimer(null);
      setElapsed(0);
    }

    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);
    const workoutTitle = workoutDetails?.title || "Exercise";
    const setNumber = setIdx + 1;

    const proceed = () => {
      if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
        workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
      }

      workout.loggedSetDetails[setIdx].completed = completedVal;

      // Check if all sets completed
      const allSetsChecked = workout.loggedSetDetails.every((s) => s.completed);
      workout.completed = allSetsChecked;

      handleUpdateWorkoutSession(sessionCopy);
    };

    if (skipConfirm) {
      proceed();
    } else {
      setConfirmDialog({
        title: completedVal ? "Complete Set" : "Uncheck Set",
        type: "set",
        action: completedVal ? "complete" : "reset",
        workoutTitle,
        setNumber,
        onConfirm: proceed,
      });
    }
  };

  const handleToggleWorkoutComplete = (
    sectionIdx,
    workoutIdx,
    completedVal,
  ) => {
    if (!localSession) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);
    const workoutTitle = workoutDetails?.title || "Exercise";

    setConfirmDialog({
      title: completedVal ? "Complete Exercise" : "Reset Exercise",
      type: "workout",
      action: completedVal ? "complete" : "reset",
      workoutTitle,
      onConfirm: () => {
        workout.completed = completedVal;

        if (
          !workout.loggedSetDetails ||
          workout.loggedSetDetails.length === 0
        ) {
          workout.loggedSetDetails = getLoggedSetDetails(
            workout,
            workoutDetails,
          );
        }

        workout.loggedSetDetails.forEach((s) => {
          s.completed = completedVal;
        });

        handleUpdateWorkoutSession(sessionCopy);
      },
    });
  };

  const handleToggleSectionComplete = (sectionIdx, completedVal) => {
    if (!localSession) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const section = sessionCopy.sections[sectionIdx];
    const sectionName = section?.name || "Section";

    setConfirmDialog({
      title: completedVal ? "Complete Section" : "Reset Section",
      type: "section",
      action: completedVal ? "complete" : "reset",
      sectionName,
      onConfirm: () => {
        section.workouts?.forEach((w) => {
          w.completed = completedVal;
          const workoutDetails = getWorkoutDetails(w.workoutId);
          if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
            w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
          }
          w.loggedSetDetails.forEach((s) => {
            s.completed = completedVal;
          });
        });

        handleUpdateWorkoutSession(sessionCopy);
      },
    });
  };

  const handleUpdateSetMetric = (
    sectionIdx,
    workoutIdx,
    setIdx,
    field,
    val,
  ) => {
    if (!localSession) return;
    const sessionCopy = JSON.parse(
      JSON.stringify(localSession.workoutSession || { sections: [] }),
    );
    const workout = sessionCopy.sections[sectionIdx].workouts[workoutIdx];
    const workoutDetails = getWorkoutDetails(workout.workoutId);

    if (!workout.loggedSetDetails || workout.loggedSetDetails.length === 0) {
      workout.loggedSetDetails = getLoggedSetDetails(workout, workoutDetails);
    }

    workout.loggedSetDetails[setIdx][field] = val;
    handleUpdateWorkoutSession(sessionCopy);
  };

  const handleMarkComplete = async () => {
    setConfirmDialog({
      title: "Complete Entire Session",
      type: "session-bulk",
      action: "complete",
      description:
        "Are you sure you want to mark this entire workout session as Completed? This will automatically mark all sections, exercises, and sets as completed.",
      onConfirm: async () => {
        const token = localStorage.getItem("member_token");
        if (!token) return;
        setLoading(true);
        try {
          if (localSession.workoutSession) {
            const sessionCopy = JSON.parse(
              JSON.stringify(localSession.workoutSession || { sections: [] }),
            );
            sessionCopy.sections?.forEach((sec) => {
              sec.workouts?.forEach((w) => {
                w.completed = true;
                if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
                  const workoutDetails = localSession.workouts?.find(
                    (dw) => dw.id === w.workoutId,
                  );
                  w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
                }
                w.loggedSetDetails.forEach((s) => {
                  s.completed = true;
                });
              });
            });
            await api.put(
              `/member-portal/workouts/schedule/${localSession.id}/session`,
              { workoutSession: sessionCopy },
              { headers: { Authorization: `Bearer ${token}` } },
            );
          }
          await api.put(
            `/member-portal/workouts/schedule/${localSession.id}/status`,
            { status: "completed" },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          toast.success("Workout marked as completed");
          onSuccess?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to update status");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleMarkUncomplete = async () => {
    setConfirmDialog({
      title: "Reset Entire Session",
      type: "session-bulk",
      action: "reset",
      description:
        "Are you sure you want to mark this entire workout session as Incomplete? This will reset all exercises and sets to incomplete.",
      onConfirm: async () => {
        const token = localStorage.getItem("member_token");
        if (!token) return;
        setLoading(true);
        try {
          if (localSession.workoutSession) {
            const sessionCopy = JSON.parse(
              JSON.stringify(localSession.workoutSession || { sections: [] }),
            );
            sessionCopy.sections?.forEach((sec) => {
              sec.workouts?.forEach((w) => {
                w.completed = false;
                if (!w.loggedSetDetails || w.loggedSetDetails.length === 0) {
                  const workoutDetails = localSession.workouts?.find(
                    (dw) => dw.id === w.workoutId,
                  );
                  w.loggedSetDetails = getLoggedSetDetails(w, workoutDetails);
                }
                w.loggedSetDetails.forEach((s) => {
                  s.completed = false;
                });
              });
            });
            await api.put(
              `/member-portal/workouts/schedule/${localSession.id}/session`,
              { workoutSession: sessionCopy },
              { headers: { Authorization: `Bearer ${token}` } },
            );
          }
          await api.put(
            `/member-portal/workouts/schedule/${localSession.id}/status`,
            { status: "upcoming" },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          toast.success("Workout marked as uncompleted");
          onSuccess?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to update status");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <>
      <Card className=" border-0 shadow-sm">
        {!hideHeader && (
          <CardHeader className="px-1 py-1 mb-2 border-b border-border/40 flex flex-row items-center justify-between gap-3">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-black flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-primary" />
              <span>{dateLabel}</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase text-muted-foreground font-mono">
                {localSession.dayOfWeek}
              </span>
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${st.light} ${st.text}`}
              >
                <Icon className="h-3 w-3" />
                {st.label}
              </span>
            </div>
          </CardHeader>
        )}

        <CardContent className="p-0 space-y-1">
          {localSession.status === "completed" &&
            localSession.completedAt &&
            (() => {
              const date = new Date(localSession.completedAt);
              const months = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ];
              const day = date.getDate();
              const month = months[date.getMonth()];
              const year = date.getFullYear();
              let hours = date.getHours();
              const minutes = String(date.getMinutes()).padStart(2, "0");
              const ampm = hours >= 12 ? "pm" : "am";
              hours = hours % 12;
              hours = hours ? hours : 12;
              const displayDateTime = `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
              const isMember = localSession.completedBy?.startsWith("member:");

              return (
                <div className="text-xs flex-col tracking-wider flex items-start text-muted-foreground font-mono space-y-0.5 border bg-secondary/10 border-border/40 p-2 rounded-lg">
                  <p className=" text-foreground">
                    Done by {isMember ? "Member" : "Staff"}:{" "}
                    {localSession.completedByName ||
                      (isMember ? "Member" : "Staff")}
                  </p>
                  <p className="text-muted-foreground font-medium">
                    {displayDateTime}
                  </p>
                </div>
              );
            })()}

          {hasWorkouts ? (
            <div className="space-y-2">
              {sectionsToRender.map((section, sIdx) => {
                const sectionWorkouts = (section.workouts || [])
                  .map((w, wIdx) => {
                    const details = getWorkoutDetails(w.workoutId);
                    return { ...w, details, wIdx };
                  })
                  .filter((w) => w.details);

                if (sectionWorkouts.length === 0) return null;

                const sectionKey =
                  section.id ||
                  `${sIdx}-${section.name}-${section.source || ""}`;
                const isCollapsed = !expandedSections[sectionKey];
                const completedCount = sectionWorkouts.filter(
                  (w) => w.completed,
                ).length;

                return (
                  <div
                    key={section.id || sIdx}
                    id={`section-container-${sectionKey}`}
                    className="space-y-2 bg-secondary/100 p-2 rounded-lg border border-border/40 select-none scroll-mt-4"
                  >
                    {/* Section Header */}
                    <div
                      onClick={() =>
                        setExpandedSections((prev) => {
                          const wasExpanded = !!prev[sectionKey];
                          if (!wasExpanded) {
                            setTimeout(() => {
                              const el = document.getElementById(
                                `section-container-${sectionKey}`,
                              );
                              el?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }, 150);
                            return { [sectionKey]: true };
                          } else {
                            return {};
                          }
                        })
                      }
                      className="flex items-center justify-between cursor-pointer hover:bg-secondary/20 px-1 rounded transition-colors group select-none"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                            isCollapsed && "-rotate-90",
                          )}
                        />
                        {allowMemberToComplete && (
                          <Checkbox
                            checked={
                              sectionWorkouts.length > 0 &&
                              sectionWorkouts.every((w) => w.completed)
                            }
                            onCheckedChange={(checked) => {
                              const secIdxInFull =
                                localSession.workoutSession?.sections?.findIndex(
                                  (sec) =>
                                    sec.name === section.name &&
                                    sec.source === section.source,
                                ) ?? -1;
                              if (secIdxInFull !== -1) {
                                handleToggleSectionComplete(
                                  secIdxInFull,
                                  !!checked,
                                );
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-primary border-muted-foreground/45 rounded mr-0.5"
                          />
                        )}
                        <h4 className="text-[12px] font-heading font-black uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <span>{section.name || "Workout Section"}</span>
                        </h4>
                      </div>
                      <div className="text-[10px] font-mono font-bold bg-secondary px-1.5 py-0.5 rounded ml-auto mr-2 shrink-0 flex items-center gap-1">
                        <span
                          className={
                            completedCount > 0
                              ? "text-emerald-500 dark:text-emerald-400"
                              : "text-muted-foreground/60"
                          }
                        >
                          {completedCount} done
                        </span>
                        <span className="text-muted-foreground/30">•</span>
                        <span
                          className={
                            sectionWorkouts.length - completedCount > 0
                              ? "text-amber-500 dark:text-amber-400"
                              : "text-muted-foreground/60"
                          }
                        >
                          {sectionWorkouts.length - completedCount} left
                        </span>
                      </div>
                      <span className="text-[9px] font-mono text-muted-foreground/60 group-hover:text-foreground transition-colors">
                        {sectionWorkouts.length}{" "}
                        {sectionWorkouts.length === 1 ? "workout" : "workouts"}
                      </span>
                    </div>

                    {/* Workouts in Section */}
                    {!isCollapsed && (
                      <div className="space-y-2">
                        {sectionWorkouts.map(
                          ({ details: workout, wIdx, ...w }) => {
                            const isWorkoutChecked = !!w.completed;
                            const loggedSets = getLoggedSetDetails(w, workout);

                            return (
                              <div
                                key={workout.id || wIdx}
                                className="border border-border/85 rounded-xl overflow-hidden bg-card shadow-sm transition-all duration-200 hover:border-border/95"
                              >
                                {/* Workout Header Row */}
                                <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border/20 bg-secondary/5">
                                  <div className="flex items-center gap-2.5">
                                    {allowMemberToComplete && (
                                      <Checkbox
                                        checked={isWorkoutChecked}
                                        onCheckedChange={(checked) => {
                                          const secIdxInFull =
                                            localSession.workoutSession?.sections?.findIndex(
                                              (sec) =>
                                                sec.name === section.name &&
                                                sec.source === section.source,
                                            ) ?? -1;
                                          if (secIdxInFull !== -1) {
                                            handleToggleWorkoutComplete(
                                              secIdxInFull,
                                              wIdx,
                                              !!checked,
                                            );
                                          }
                                        }}
                                        className="h-4 w-4 text-primary border-muted-foreground/45 rounded"
                                      />
                                    )}
                                    <div className="flex items-center gap-2">
                                      <div className="h-5.5 w-5.5 rounded bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                                        {workout.image ? (
                                          <img
                                            src={`${BACKEND_URL}${workout.image}`}
                                            alt={workout.title}
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          <Dumbbell className="h-2.5 w-2.5 text-muted-foreground" />
                                        )}
                                      </div>
                                      <span className="font-semibold text-xs text-foreground">
                                        {workout.title}
                                      </span>
                                    </div>
                                  </div>

                                  {(() => {
                                    const group =
                                      workout.groupNames &&
                                      workout.groupNames.length > 0
                                        ? workout.groupNames.join(", ")
                                        : workout.workoutGroup;
                                    const type =
                                      workout.typeNames &&
                                      workout.typeNames.length > 0
                                        ? workout.typeNames.join(", ")
                                        : workout.workoutType;

                                    if (group || type) {
                                      return (
                                        <div className="flex gap-1.5">
                                          {group && (
                                            <Badge
                                              variant="outline"
                                              className="text-[8px] font-mono uppercase px-1 py-0 bg-blue-500/5 text-blue-500 border-blue-500/10"
                                            >
                                              {group}
                                            </Badge>
                                          )}
                                          {type && (
                                            <Badge
                                              variant="outline"
                                              className="text-[8px] font-mono uppercase px-1 py-0 bg-primary/5 text-primary border-primary/10"
                                            >
                                              {type}
                                            </Badge>
                                          )}
                                        </div>
                                      );
                                    }

                                    return (
                                      <Badge
                                        variant="outline"
                                        className="text-[8px] font-mono uppercase px-1 py-0"
                                      >
                                        {workout.workoutMetric === "reps"
                                          ? "Reps & Weight"
                                          : workout.workoutMetric || "reps"}
                                      </Badge>
                                    );
                                  })()}
                                </div>

                                {/* Workout Content */}
                                <div className="p-2 space-y-2">
                                  {/* Instructions */}
                                  {(workout.description ||
                                    workout.instructions) && (
                                    <div className="text-[10px] text-muted-foreground/85 leading-relaxed border-l border-muted/20 pl-1.5">
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            workout.description ||
                                            workout.instructions,
                                        }}
                                      />
                                    </div>
                                  )}

                                  {/* Subtask Sets Checklist */}
                                  {loggedSets.length > 0 && (
                                    <div className="space-y-1">
                                      <p className="text-[8.5px] font-mono uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
                                        <span>
                                          Total Sets ({loggedSets.length})
                                        </span>
                                        {allowMemberToComplete && (
                                          <>
                                            <span className="text-muted-foreground/30 font-normal">
                                              |
                                            </span>
                                            <span className="text-primary/70">
                                              Customize the values
                                            </span>
                                          </>
                                        )}
                                      </p>

                                      <div className="space-y-0.5">
                                        {loggedSets.map((set, sIdx) => {
                                          let suggestText = "";
                                          if (
                                            workout.workoutMetric === "reps"
                                          ) {
                                            suggestText = `${set.reps || 10} reps ${set.weightKg ? `@${set.weightKg} kg` : ""}`;
                                          } else if (
                                            workout.workoutMetric === "duration"
                                          ) {
                                            suggestText = `${set.durationMin || 1} mins`;
                                          } else if (
                                            workout.workoutMetric === "distance"
                                          ) {
                                            suggestText = `${set.distanceKm || 1} kms`;
                                          } else if (
                                            workout.workoutMetric === "count"
                                          ) {
                                            suggestText = `${set.count || 10} counts`;
                                          }

                                          const isSetChecked = !!set.completed;
                                          const secIdxInFull =
                                            localSession.workoutSession?.sections?.findIndex(
                                              (sec) =>
                                                sec.name === section.name &&
                                                sec.source === section.source,
                                            ) ?? -1;

                                          return (
                                            <div
                                              key={sIdx}
                                              className={`flex flex-wrap items-center justify-between gap-1 p-1 px-1.5 rounded-lg border text-[11px] select-none transition-all duration-200 ${
                                                isSetChecked
                                                  ? "bg-emerald-500/5 border-emerald-500/20 text-foreground"
                                                  : "bg-secondary/20 border-border/40 text-muted-foreground"
                                              }`}
                                            >
                                              <div
                                                className={cn(
                                                  "flex items-center gap-1.5 py-0.5",
                                                  allowMemberToComplete &&
                                                    "cursor-pointer",
                                                )}
                                                onClick={() => {
                                                  if (
                                                    allowMemberToComplete &&
                                                    secIdxInFull !== -1
                                                  ) {
                                                    handleToggleSetComplete(
                                                      secIdxInFull,
                                                      wIdx,
                                                      sIdx,
                                                      !isSetChecked,
                                                    );
                                                  }
                                                }}
                                              >
                                                {allowMemberToComplete && (
                                                  <Checkbox
                                                    checked={isSetChecked}
                                                    onCheckedChange={(
                                                      checked,
                                                    ) => {
                                                      if (secIdxInFull !== -1) {
                                                        handleToggleSetComplete(
                                                          secIdxInFull,
                                                          wIdx,
                                                          sIdx,
                                                          !!checked,
                                                        );
                                                      }
                                                    }}
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    className="h-3.5 w-3.5 text-emerald-500 border-muted-foreground/30 rounded-sm pointer-events-none"
                                                  />
                                                )}
                                                <span className="font-mono text-[9.5px] font-bold text-foreground">
                                                  S{sIdx + 1}
                                                </span>
                                                {suggestText && (
                                                  <span className="text-[8.5px] text-muted-foreground">
                                                    ({suggestText})
                                                  </span>
                                                )}
                                                {allowMemberToComplete &&
                                                  enableTimer &&
                                                  secIdxInFull !== -1 && (
                                                    <div
                                                      className="flex items-center gap-1.5 ml-1.5"
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      {activeTimer &&
                                                      activeTimer.secIdx ===
                                                        secIdxInFull &&
                                                      activeTimer.wIdx ===
                                                        wIdx &&
                                                      activeTimer.setIdx ===
                                                        sIdx ? (
                                                        <div className="flex items-center gap-1.5">
                                                          <span className="text-orange-500 font-mono text-[9.5px] font-bold animate-pulse bg-orange-500/10 px-1 py-0.5 rounded border border-orange-500/20">
                                                            {formatTime(
                                                              elapsed,
                                                            )}
                                                          </span>
                                                          <button
                                                            onClick={() =>
                                                              handleStopTimerAndComplete(
                                                                secIdxInFull,
                                                                wIdx,
                                                                sIdx,
                                                              )
                                                            }
                                                            className="px-1.5 py-0.5 rounded bg-red-500 hover:bg-red-600 text-white text-[9px] font-medium flex items-center gap-0.5 transition-colors border-0 shrink-0 cursor-pointer"
                                                          >
                                                            <Square className="h-2 w-2 fill-white text-white" />
                                                            Stop & Complete
                                                          </button>
                                                        </div>
                                                      ) : (
                                                        !isSetChecked && (
                                                          <button
                                                            onClick={() =>
                                                              handleStartTimer(
                                                                secIdxInFull,
                                                                wIdx,
                                                                sIdx,
                                                              )
                                                            }
                                                            className="px-1.5 py-0.5 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-[9px] font-medium flex items-center gap-0.5 transition-colors border-0 shrink-0 cursor-pointer"
                                                          >
                                                            <Play className="h-2 w-2 fill-current" />
                                                            Start
                                                          </button>
                                                        )
                                                      )}
                                                    </div>
                                                  )}
                                              </div>

                                              <div className="flex items-center gap-1 text-[10px]">
                                                {workout.workoutMetric ===
                                                  "reps" && (
                                                  <>
                                                    <div className="flex items-center gap-0.5 w-full">
                                                      <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        disabled={
                                                          !allowMemberToComplete
                                                        }
                                                        value={
                                                          set.reps !== undefined
                                                            ? set.reps
                                                            : ""
                                                        }
                                                        onChange={(e) => {
                                                          const val =
                                                            e.target.value;
                                                          if (
                                                            secIdxInFull !== -1
                                                          ) {
                                                            handleUpdateSetMetric(
                                                              secIdxInFull,
                                                              wIdx,
                                                              sIdx,
                                                              "reps",
                                                              val === ""
                                                                ? ""
                                                                : parseInt(
                                                                    val,
                                                                  ) || 0,
                                                            );
                                                          }
                                                        }}
                                                        className="w-[60px] h-5 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                      />
                                                      <span className="text-[9.5px]">
                                                        r
                                                      </span>
                                                    </div>
                                                    <span>@</span>
                                                    <div className="flex items-center gap-0.5 w-full">
                                                      <input
                                                        type="number"
                                                        inputMode="decimal"
                                                        disabled={
                                                          !allowMemberToComplete
                                                        }
                                                        value={
                                                          set.weightKg !==
                                                          undefined
                                                            ? set.weightKg
                                                            : ""
                                                        }
                                                        onChange={(e) => {
                                                          const val =
                                                            e.target.value;
                                                          if (
                                                            secIdxInFull !== -1
                                                          ) {
                                                            handleUpdateSetMetric(
                                                              secIdxInFull,
                                                              wIdx,
                                                              sIdx,
                                                              "weightKg",
                                                              val === ""
                                                                ? ""
                                                                : parseFloat(
                                                                    val,
                                                                  ) || 0,
                                                            );
                                                          }
                                                        }}
                                                        className="w-[60px] h-5 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                      />
                                                      <span className="text-[9.5px]">
                                                        kg
                                                      </span>
                                                    </div>
                                                  </>
                                                )}

                                                {workout.workoutMetric ===
                                                  "duration" && (
                                                  <div className="flex items-center gap-0.5 w-full">
                                                    <input
                                                      type="number"
                                                      inputMode="decimal"
                                                      disabled={
                                                        !allowMemberToComplete
                                                      }
                                                      value={
                                                        set.durationMin !==
                                                        undefined
                                                          ? set.durationMin
                                                          : ""
                                                      }
                                                      onChange={(e) => {
                                                        const val =
                                                          e.target.value;
                                                        if (
                                                          secIdxInFull !== -1
                                                        ) {
                                                          handleUpdateSetMetric(
                                                            secIdxInFull,
                                                            wIdx,
                                                            sIdx,
                                                            "durationMin",
                                                            val === ""
                                                              ? ""
                                                              : parseFloat(
                                                                  val,
                                                                ) || 0,
                                                          );
                                                        }
                                                      }}
                                                      className="w-[60px] h-5 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                    />
                                                    <span className="text-[9.5px]">
                                                      mins
                                                    </span>
                                                  </div>
                                                )}

                                                {workout.workoutMetric ===
                                                  "distance" && (
                                                  <div className="flex items-center gap-0.5 w-full">
                                                    <input
                                                      type="number"
                                                      inputMode="decimal"
                                                      disabled={
                                                        !allowMemberToComplete
                                                      }
                                                      value={
                                                        set.distanceKm !==
                                                        undefined
                                                          ? set.distanceKm
                                                          : ""
                                                      }
                                                      onChange={(e) => {
                                                        const val =
                                                          e.target.value;
                                                        if (
                                                          secIdxInFull !== -1
                                                        ) {
                                                          handleUpdateSetMetric(
                                                            secIdxInFull,
                                                            wIdx,
                                                            sIdx,
                                                            "distanceKm",
                                                            val === ""
                                                              ? ""
                                                              : parseFloat(
                                                                  val,
                                                                ) || 0,
                                                          );
                                                        }
                                                      }}
                                                      className="w-[60px] h-5 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                    />
                                                    <span className="text-[9.5px]">
                                                      km
                                                    </span>
                                                  </div>
                                                )}

                                                {workout.workoutMetric ===
                                                  "count" && (
                                                  <div className="flex items-center gap-0.5">
                                                    <input
                                                      type="number"
                                                      inputMode="numeric"
                                                      pattern="[0-9]*"
                                                      disabled={
                                                        !allowMemberToComplete
                                                      }
                                                      value={
                                                        set.count !== undefined
                                                          ? set.count
                                                          : ""
                                                      }
                                                      onChange={(e) => {
                                                        const val =
                                                          e.target.value;
                                                        if (
                                                          secIdxInFull !== -1
                                                        ) {
                                                          handleUpdateSetMetric(
                                                            secIdxInFull,
                                                            wIdx,
                                                            sIdx,
                                                            "count",
                                                            val === ""
                                                              ? ""
                                                              : parseInt(val) ||
                                                                  0,
                                                          );
                                                        }
                                                      }}
                                                      className="w-[60px] h-5 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                    />
                                                    <span className="text-[9.5px]">
                                                      counts
                                                    </span>
                                                  </div>
                                                )}

                                                {set.restSeconds &&
                                                  set.restSeconds !== "none" &&
                                                  Number(set.restSeconds) >
                                                    0 && (
                                                    <div className="flex items-center gap-1 pl-1 border-l border-border/30">
                                                      <span className="text-[8.5px] text-amber-500 font-mono">
                                                        Rest:
                                                      </span>
                                                      <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        disabled={
                                                          !allowMemberToComplete
                                                        }
                                                        value={
                                                          set.restSeconds !==
                                                          undefined
                                                            ? set.restSeconds
                                                            : 0
                                                        }
                                                        onChange={(e) => {
                                                          const val =
                                                            e.target.value;
                                                          if (
                                                            secIdxInFull !== -1
                                                          ) {
                                                            handleUpdateSetMetric(
                                                              secIdxInFull,
                                                              wIdx,
                                                              sIdx,
                                                              "restSeconds",
                                                              val === ""
                                                                ? ""
                                                                : parseInt(
                                                                    val,
                                                                  ) || 0,
                                                            );
                                                          }
                                                        }}
                                                        className="w-[60px] h-5 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                      />
                                                      <span className="text-[9px]">
                                                        s
                                                      </span>
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* YouTube Link */}
                                  {workout.youtubeLink && (
                                    <div className="flex items-center gap-1.5 bg-red-500/5 hover:bg-red-500/10 transition-colors px-2.5 py-1 rounded border border-red-500/10 w-fit mt-1.5">
                                      <svg
                                        className="h-3 w-3 text-red-500 shrink-0"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M23.498 6.163a3.003 3.003 0 0 0-2.11-2.108C19.52 3.5 12 3.5 12 3.5s-7.52 0-9.388.555A3.002 3.002 0 0 0 .502 6.163C0 8.07 0 12 0 12s0 3.93 .502 5.837a3.003 3.003 0 0 0 2.11 2.108C4.48 20.5 12 20.5 12 20.5s7.52 0 9.388-.555a3.003 3.003 0 0 0 2.11-2.108C24 15.93 24 12 24 12s0-3.93-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                      </svg>
                                      <a
                                        href={workout.youtubeLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9.5px] text-red-600 dark:text-red-400 hover:underline flex items-center gap-0.5 font-semibold"
                                      >
                                        Video Tutorial{" "}
                                        <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
                                      </a>
                                    </div>
                                  )}

                                  {/* Attachments */}
                                  {workout.attachments &&
                                    workout.attachments.length > 0 && (
                                      <div className="space-y-1 mt-1.5">
                                        <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                                          Attachments
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {workout.attachments.map(
                                            (file, fIdx) => {
                                              const fileUrl =
                                                file.url.startsWith("http")
                                                  ? file.url
                                                  : `${BACKEND_URL}${file.url}`;
                                              const fileIsImage = isImage(file);
                                              return fileIsImage ? (
                                                <button
                                                  key={fIdx}
                                                  onClick={() => {
                                                    setPreviewDialog({
                                                      open: true,
                                                      src: fileUrl,
                                                      name:
                                                        file.name ||
                                                        "Image Attachment",
                                                    });
                                                  }}
                                                  className="flex items-center gap-1.5 text-[9px] bg-card hover:bg-secondary/40 border border-border px-2 py-0.5 rounded-md text-muted-foreground hover:text-foreground font-medium transition-colors cursor-pointer"
                                                >
                                                  <span className="truncate max-w-[100px]">
                                                    {file.name || "Attachment"}
                                                  </span>
                                                </button>
                                              ) : (
                                                <a
                                                  key={fIdx}
                                                  href={fileUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="flex items-center gap-1.5 text-[9px] bg-card hover:bg-secondary/40 border border-border px-2 py-0.5 rounded-md text-muted-foreground hover:text-foreground font-medium transition-colors"
                                                >
                                                  <span className="truncate max-w-[100px]">
                                                    {file.name || "Attachment"}
                                                  </span>
                                                </a>
                                              );
                                            },
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No workouts listed for this day.
            </p>
          )}

          {localSession.status === "upcoming" && allowMemberToComplete && (
            <button
              onClick={handleMarkComplete}
              disabled={loading}
              className="w-full py-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 disabled:opacity-50 text-xs font-bold transition-colors mt-4 mb-2 border-0"
            >
              {loading ? (
                "Updating..."
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Mark all as Completed
                </>
              )}
            </button>
          )}

          {localSession.status === "completed" && allowMemberToComplete && (
            <button
              onClick={handleMarkUncomplete}
              disabled={loading}
              className="w-full py-1.5 flex items-center justify-center gap-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 disabled:opacity-50 text-xs font-bold transition-colors mt-2 mb-2 border-0"
            >
              {loading ? (
                "Updating..."
              ) : (
                <>
                  <XCircle className="h-3.5 w-3.5" />
                  Mark as Uncompleted
                </>
              )}
            </button>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!confirmDialog}
        onOpenChange={(open) => {
          if (!open) setConfirmDialog(null);
        }}
      >
        <DialogContent className="max-w-sm bg-card border-border p-5 rounded-lg flex flex-col gap-4">
          <DialogHeader className="text-left space-y-1.5 shrink-0">
            <DialogTitle className="font-heading uppercase tracking-wide text-xs font-black text-foreground">
              {confirmDialog?.title}
            </DialogTitle>
            <DialogDescription className="text-[11.5px] text-muted-foreground leading-relaxed flex flex-col gap-2">
              {confirmDialog?.type === "timer" && (
                <div className="flex flex-col gap-2">
                  <p>Awesome effort! You finished your set.</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.workoutTitle}
                    </span>
                    <span className="text-orange-500 font-mono text-[10px] font-bold bg-orange-500/10 px-1.5 py-0.5 rounded border border-orange-500/20">
                      {confirmDialog.formattedTime}
                    </span>
                  </div>
                  <p className="font-medium text-foreground">
                    Would you like to mark this set as completed?
                  </p>
                </div>
              )}
              {confirmDialog?.type === "set" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this set's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[9.5px] font-bold bg-primary/15 text-primary px-1.5 py-0.5 rounded border border-primary/25">
                        Set {confirmDialog.setNumber}
                      </span>
                      <span className="font-semibold text-foreground text-xs">
                        {confirmDialog.workoutTitle}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full font-heading",
                        confirmDialog.action === "complete"
                          ? "bg-green-500/15 text-green-600 dark:text-green-400"
                          : "bg-amber-500/15 text-amber-600 dark:text-amber-400",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Complete"
                        : "Incomplete"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "workout" && (
                <div className="flex flex-col gap-2">
                  <p>
                    Are you sure you want to update all sets for this exercise?
                  </p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded bg-secondary/60 flex items-center justify-center shrink-0">
                        <Dumbbell className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <span className="font-semibold text-foreground text-xs">
                        {confirmDialog.workoutTitle}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full font-heading",
                        confirmDialog.action === "complete"
                          ? "bg-green-500/15 text-green-600 dark:text-green-400"
                          : "bg-amber-500/15 text-amber-600 dark:text-amber-400",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Complete All"
                        : "Reset All"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "section" && (
                <div className="flex flex-col gap-2">
                  <p>
                    Are you sure you want to update all exercises in this
                    section?
                  </p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-heading font-black uppercase tracking-wider text-[11px] text-foreground">
                      {confirmDialog.sectionName}
                    </span>
                    <span
                      className={cn(
                        "text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full font-heading",
                        confirmDialog.action === "complete"
                          ? "bg-green-500/15 text-green-600 dark:text-green-400"
                          : "bg-amber-500/15 text-amber-600 dark:text-amber-400",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Complete Section"
                        : "Reset Section"}
                    </span>
                  </div>
                </div>
              )}
              {(confirmDialog?.type === "session-auto" ||
                confirmDialog?.type === "session-bulk") && (
                <div className="flex flex-col gap-2">
                  <p className="whitespace-pre-line">
                    {confirmDialog.description}
                  </p>
                </div>
              )}
              {!confirmDialog?.type && confirmDialog?.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex items-center flex-row justify-end gap-2 shrink-0">
            <button
              onClick={() => setConfirmDialog(null)}
              className="px-4 py-1.5 text-[10px] font-heading font-black uppercase tracking-wider bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors border-0"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                confirmDialog?.onConfirm?.();
                setConfirmDialog(null);
              }}
              className="px-4 py-1.5 text-[10px] font-heading font-black uppercase tracking-wider bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors border-0"
            >
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewDialog.open}
        onOpenChange={(o) => setPreviewDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="bg-card border-border max-w-sm p-4 rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-black text-foreground uppercase tracking-wide">
              {previewDialog.name}
            </DialogTitle>
          </DialogHeader>
          <img
            src={previewDialog.src}
            alt={previewDialog.name}
            className="rounded-sm max-h-80 w-full object-contain border border-border mt-2"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── AllSchedules ──────────────────────────────────────────────────────────────
function AllSchedules({
  schedule,
  onSelectSession,
  selectedDateStr,
  allowMemberToComplete,
  enableTimer = false,
  onSuccess,
  hasUnresolvedMissed = false,
  todayStr,
  isFlexible = false,
  workoutCard = {},
  missedWorkoutPolicy = "skip_missed_workout",
  missedCount = 0,
  fitnessPreparationDays = 0,
  fitnessRePreparationDays = 0,
}) {
  const [filter, setFilter] = useState("all");
  const [activeItem, setActiveItem] = useState("");
  const isFirstMount = useRef(true);
  const filters = ["all", "upcoming", "completed", "missed", "skipped"];
  const filtered =
    filter === "all" ? schedule : schedule.filter((s) => s.status === filter);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setActiveItem(selectedDateStr);
  }, [selectedDateStr]);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-0 pt-4 px-4">
        <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-3">
          All Schedules
        </CardTitle>
        {/* Filter pills — full-width row, no wrap overflow */}
        <div className="flex w-full rounded-lg overflow-hidden border border-border text-xs">
          {filters.map((f, i) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={[
                "flex-1 py-1.5 capitalize font-medium transition-colors text-center",
                i < filters.length - 1 ? "border-r border-border" : "",
                filter === f
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {f}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="px-0.5">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
            <Clock className="h-7 w-7 opacity-40" />
            <p className="text-sm">No sessions found</p>
          </div>
        ) : (
          <Accordion
            type="single"
            collapsible
            value={activeItem || ""}
            onValueChange={(val) => {
              setActiveItem(val);
              if (val) {
                const found = filtered.find((s) => s.date === val);
                if (found) onSelectSession(found);
              }
            }}
            className="w-full"
          >
            {filtered.map((s, i) => {
              const st = STATUS_CLS[s.status] || STATUS_CLS.upcoming;
              const Icon = st.icon;
              const dateLabel = s.date
                ? parseLocalDate(s.date)?.toLocaleDateString("en-IN", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })
                : s.date;

              const isItemLocked =
                !isFlexible &&
                allowMemberToComplete &&
                hasUnresolvedMissed &&
                s.date >= todayStr &&
                s.status === "upcoming";

              return (
                <AccordionItem key={i} value={s.date || `${i}`} className="">
                  <AccordionTrigger className="hover:no-underline py-3 px-2">
                    <div className="flex flex-col items-start text-left min-w-0 w-full px-2">
                      <div className="flex items-center justify-between w-full gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className={`h-4 w-4 shrink-0 ${st.text}`} />
                          <span className="text-sm font-medium truncate">
                            {dateLabel}
                          </span>
                          <span className="text-[10px] uppercase text-muted-foreground font-mono shrink-0">
                            {s.dayOfWeek?.slice(0, 3)}
                          </span>
                        </div>
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${st.light} ${st.text}`}
                        >
                          {st.label}
                        </span>
                      </div>
                      {s.status === "completed" && s.completedAt && (
                        <div className="text-[10px] text-muted-foreground pl-6 truncate mb-1">
                          Done • {format(new Date(s.completedAt), "PPpp")}
                        </div>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-3 px-1">
                    <DayDetailInline
                      session={s}
                      allowMemberToComplete={allowMemberToComplete}
                      enableTimer={enableTimer}
                      onSuccess={onSuccess}
                      hideHeader={true}
                      isLocked={isItemLocked}
                      isFlexible={isFlexible}
                      workoutCard={workoutCard}
                      missedWorkoutPolicy={missedWorkoutPolicy}
                      missedCount={missedCount}
                      fitnessPreparationDays={fitnessPreparationDays}
                      fitnessRePreparationDays={fitnessRePreparationDays}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

// ─── WeeklyPattern ─────────────────────────────────────────────────────────────
function WeeklyPattern({ weekPlan }) {
  if (!Object.keys(weekPlan).length) return null;
  return (
    <Card className="bg-card border-border shadow-sm">
      <CardContent className="p-2.5">
        <p className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mb-2">
          Weekly Pattern
        </p>

        {/* 7-col grid — equal columns, no gap overflow */}
        <div className="grid grid-cols-7">
          {DAY_ORDER.map((day) => {
            const wks = weekPlan[day] || [];
            const active = wks.length > 0;
            return (
              <div
                key={day}
                className={`flex flex-col items-center gap-1 py-2 mx-px rounded-lg ${
                  active ? "bg-primary/10" : "bg-secondary"
                }`}
              >
                <span
                  className={`text-[10px] font-mono font-bold leading-none ${
                    active ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {DAY_LABEL[day][0]}
                </span>
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    active ? "bg-primary" : "bg-border"
                  }`}
                />
                {active && (
                  <span className="text-[8px] text-primary font-mono font-medium leading-none">
                    {wks.length}W
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Workout name list below the grid */}
        <div className="mt-3 space-y-1.5">
          {DAY_ORDER.filter((d) => (weekPlan[d] || []).length > 0).map(
            (day) => (
              <div key={day} className="flex items-start gap-2">
                <span className="text-[10px] font-mono uppercase text-muted-foreground w-6 pt-0.5 shrink-0">
                  {DAY_LABEL[day][0]}
                </span>
                <div className="flex flex-wrap gap-1 min-w-0">
                  {(weekPlan[day] || []).map((w, i) => (
                    <span
                      key={i}
                      className="text-[10px] bg-secondary text-foreground px-1.5 py-0.5 rounded font-medium"
                    >
                      {w.title}
                    </span>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function MemberWorkouts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const tab = searchParams.get("tab") || "calendar";
  const cardIdParam = searchParams.get("cardId") || "";
  const setTab = (newTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", newTab);
        return next;
      },
      { replace: true },
    );
  };
  const [scannerOpen, setScannerOpen] = useState(false);
  const workoutDetailRef = useRef(null);

  // ── Self-workout state ──────────────────────────────────────────────────
  const [memberSettings, setMemberSettings] = useState({
    allowMemberSelfWorkout: false,
  });
  const [selfWorkoutCards, setSelfWorkoutCards] = useState([]);
  const [selectedSelfWorkoutId, setSelectedSelfWorkoutId] = useState("");
  const [selfSessions, setSelfSessions] = useState([]);
  const [selfSessionsLoading, setSelfSessionsLoading] = useState(false);
  // Create card dialog
  const [createCardDialog, setCreateCardDialog] = useState(false);
  const [newCardName, setNewCardName] = useState("");
  const [creatingCard, setCreatingCard] = useState(false);
  // Add session dialog
  const [addSessionDialog, setAddSessionDialog] = useState(false);
  const [addSessionSections, setAddSessionSections] = useState({
    Warmup: true,
    Workouts: true,
    Cooldown: false,
  });
  const [customSections, setCustomSections] = useState([]);
  const [editingSessionForSections, setEditingSessionForSections] =
    useState(null);
  const [dialogSections, setDialogSections] = useState([]);
  const [addingSession, setAddingSession] = useState(false);

  useEffect(() => {
    if (addSessionDialog) {
      if (editingSessionForSections) {
        const existingSections =
          editingSessionForSections.workoutSession?.sections || [];
        const existingNames = existingSections.map((s) => s.name);

        const allPossible = [
          "Warmup",
          "Workouts",
          "Cooldown",
          ...(memberSettings.customSections || []),
        ];
        const remainingNames = allPossible.filter(
          (name) => !existingNames.includes(name),
        );
        const combinedList = [...existingNames, ...remainingNames];

        setDialogSections(combinedList);

        const initialChecked = {};
        combinedList.forEach((name) => {
          initialChecked[name] = existingNames.includes(name);
        });
        setAddSessionSections(initialChecked);
        setCustomSections([]);
      } else {
        const defaultList = [
          "Warmup",
          "Workouts",
          "Cooldown",
          ...(memberSettings.customSections || []),
        ];
        setDialogSections(defaultList);

        const initialChecked = {
          Warmup: true,
          Workouts: true,
          Cooldown: false,
        };
        (memberSettings.customSections || []).forEach((name) => {
          initialChecked[name] = false;
        });
        setAddSessionSections(initialChecked);
        setCustomSections([]);
      }
    }
  }, [
    addSessionDialog,
    editingSessionForSections,
    memberSettings.customSections,
  ]);

  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const selectedDateStr = searchParams.get("date") || todayStr;
  const monthParam = searchParams.get("month");

  const initialMonth = useMemo(() => {
    if (monthParam) {
      const [y, m] = monthParam.split("-").map(Number);
      if (!isNaN(y) && !isNaN(m)) {
        return new Date(y, m - 1, 1);
      }
    }
    if (selectedDateStr) {
      const [y, m] = selectedDateStr.split("-").map(Number);
      if (!isNaN(y) && !isNaN(m)) {
        return new Date(y, m - 1, 1);
      }
    }
    return new Date();
  }, [monthParam, selectedDateStr]);

  const [currentMonth, setCurrentMonth] = useState(initialMonth);

  useEffect(() => {
    setCurrentMonth(initialMonth);
  }, [initialMonth]);

  const missedWorkoutPolicy =
    data?.assignment?.missedWorkoutPolicy || "skip_missed_workout";
  const fitnessRePreparationDays = data?.assignment?.fitnessRePreparationDays;
  const allowMemberToComplete = data?.assignment?.allowMemberToComplete;

  const unresolvedMissedDays = useMemo(() => {
    const schedule = data?.schedule || [];
    return schedule.filter((s) => s.isUnresolved);
  }, [data?.schedule]);

  const missedCount = useMemo(() => {
    const schedule = data?.schedule || [];
    return schedule.filter(
      (s) => (s.status === "skipped" || s.isUnresolved) && s.phase !== "rest",
    ).length;
  }, [data?.schedule]);

  const unresolvedMissedCount = useMemo(() => {
    const schedule = data?.schedule || [];
    return schedule.filter((s) => s.isUnresolved).length;
  }, [data?.schedule]);

  const isRePrepLocked = useMemo(() => {
    if (data?.assignment?.isFlexible) return false;
    return !!(
      fitnessRePreparationDays && missedCount >= fitnessRePreparationDays
    );
  }, [fitnessRePreparationDays, missedCount, data?.assignment?.isFlexible]);

  const hasUnresolvedMissed = unresolvedMissedCount > 0;

  const activeSelectedDateStr = useMemo(() => {
    if (isRePrepLocked && unresolvedMissedDays.length > 0) {
      const schedule = data?.schedule || [];
      const cur = schedule.find((s) => s.date === selectedDateStr);
      if (
        !cur ||
        (selectedDateStr >= todayStr &&
          cur.status !== "completed" &&
          cur.status !== "missed")
      ) {
        return unresolvedMissedDays[0].date;
      }
    }
    return selectedDateStr;
  }, [
    isRePrepLocked,
    unresolvedMissedDays,
    selectedDateStr,
    todayStr,
    data?.schedule,
  ]);

  const filteredSchedule = useMemo(() => {
    const schedule = data?.schedule || [];
    if (isRePrepLocked) {
      return schedule.filter((s) => {
        const isTodayOrFuture = s.date >= todayStr;
        if (isTodayOrFuture) {
          return s.status === "completed" || s.status === "missed";
        }
        return true;
      });
    }
    return schedule;
  }, [data?.schedule, isRePrepLocked, todayStr]);

  const selectedSession = useMemo(() => {
    const schedule = data?.schedule || [];
    return schedule.find((s) => s.date === activeSelectedDateStr);
  }, [data?.schedule, activeSelectedDateStr]);

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("month", format(newMonth, "yyyy-MM"));
        return next;
      },
      { replace: true },
    );
  };

  const handleDateChange = (newDateStr) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("date", newDateStr);
        const [y, m] = newDateStr.split("-").map(Number);
        if (!isNaN(y) && !isNaN(m)) {
          next.set("month", `${y}-${String(m).padStart(2, "0")}`);
        }
        return next;
      },
      { replace: true },
    );
    setTimeout(() => {
      workoutDetailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const fetchWorkouts = async (assignmentId = null) => {
    const token = localStorage.getItem("member_token");
    if (!token) {
      navigate("/");
      return;
    }
    const currentAssignmentId = assignmentId || selectedAssignmentId;
    try {
      const res = await api.get("/member-portal/workouts", {
        params: currentAssignmentId
          ? { assignmentId: currentAssignmentId }
          : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      const allAssignments = res.data.assignments || [];
      if (!currentAssignmentId) {
        if (cardIdParam && allAssignments.find((a) => a.id === cardIdParam)) {
          // Restore a specific trainer card from URL
          setSelectedAssignmentId(cardIdParam);
        } else if (!cardIdParam && res.data.assignment) {
          // No cardId in URL — default to the active assignment
          setSelectedAssignmentId(res.data.assignment.id);
        }
        // If cardIdParam is a self-workout card ID, fetchSelfWorkoutCards will handle it
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/");
        return;
      }
      setError("Failed to load workouts.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentChange = async (id) => {
    setLoading(true);
    try {
      await api.put(
        "/member-portal/member-settings/last-card",
        { cardId: id },
        { headers: authHeaders() },
      );
    } catch {
      /* silent */
    }

    const selfCard = selfWorkoutCards.find((c) => c.id === id);
    if (selfCard) {
      // Single setSearchParams call to avoid race between cardId and tab writes
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("cardId", id);
          next.set("tab", "calendar");
          return next;
        },
        { replace: true },
      );
      setSelectedSelfWorkoutId(id);
      setSelectedAssignmentId("");
      fetchSelfSessions(id);
    } else {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("cardId", id);
          return next;
        },
        { replace: true },
      );
      setSelectedSelfWorkoutId("");
      setSelectedAssignmentId(id);
      fetchWorkouts(id);
    }
  };

  // ── Self-workout helpers ────────────────────────────────────────────────
  const token = () => localStorage.getItem("member_token");
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const fetchMemberSettings = async () => {
    try {
      const res = await api.get("/member-portal/member-settings", {
        headers: authHeaders(),
      });
      setMemberSettings(res.data || {});
    } catch {
      /* silent */
    }
  };

  const fetchSelfWorkoutCards = async () => {
    try {
      const res = await api.get("/member-portal/self-workouts", {
        headers: authHeaders(),
      });
      const cards = res.data?.selfWorkouts || [];
      setSelfWorkoutCards(cards);
      // Restore self-workout card selection from URL
      if (cardIdParam && cards.find((c) => c.id === cardIdParam)) {
        setSelectedSelfWorkoutId(cardIdParam);
        setSelectedAssignmentId("");
        fetchSelfSessions(cardIdParam);
      }
    } catch {
      /* 403 if not enabled — ignore */
    }
  };

  const fetchSelfSessions = async (cardId) => {
    setSelfSessionsLoading(true);
    try {
      const res = await api.get(
        `/member-portal/self-workouts/${cardId}/sessions`,
        { headers: authHeaders() },
      );
      setSelfSessions(res.data?.sessions || []);
    } catch {
      setSelfSessions([]);
    } finally {
      setSelfSessionsLoading(false);
      setLoading(false);
    }
  };

  const handleCreateCard = async () => {
    if (!newCardName.trim()) return;
    setCreatingCard(true);
    try {
      const res = await api.post(
        "/member-portal/self-workouts",
        { cardName: newCardName.trim() },
        { headers: authHeaders() },
      );
      toast.success("Workout card created!");
      const newCard = res.data?.selfWorkout;
      setNewCardName("");
      setCreateCardDialog(false);
      if (newCard && newCard.id) {
        setSelfWorkoutCards((prev) => [...prev, newCard]);
        await handleAssignmentChange(newCard.id);
      } else {
        await fetchSelfWorkoutCards();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create card");
    } finally {
      setCreatingCard(false);
    }
  };

  const handleAddSession = async () => {
    if (!selectedSelfWorkoutId || !selectedDateStr) return;
    setAddingSession(true);
    const sections = [];

    if (editingSessionForSections) {
      const existingSections =
        editingSessionForSections.workoutSession?.sections || [];

      // Add checked sections in the order specified in dialogSections, preserving exercises inside existing sections
      dialogSections.forEach((name) => {
        if (addSessionSections[name]) {
          const existing = existingSections.find((s) => s.name === name);
          if (existing) {
            sections.push(existing);
          } else {
            sections.push({ id: uuidv4(), name, workouts: [] });
          }
        }
      });

      // Add newly typed custom sections
      customSections.forEach((c) => {
        const trimmed = c.trim();
        if (
          trimmed &&
          !sections.some((s) => s.name.toLowerCase() === trimmed.toLowerCase())
        ) {
          const existing = existingSections.find(
            (s) => s.name.toLowerCase() === trimmed.toLowerCase(),
          );
          if (existing) {
            sections.push(existing);
          } else {
            sections.push({ id: uuidv4(), name: trimmed, workouts: [] });
          }
        }
      });

      const updatedWorkoutSession = {
        ...editingSessionForSections.workoutSession,
        sections,
      };

      try {
        await handleUpdateSelfSession(
          editingSessionForSections.id,
          updatedWorkoutSession,
        );
        toast.success("Sections updated!");
        setAddSessionDialog(false);
        setEditingSessionForSections(null);
        fetchMemberSettings();
      } catch (err) {
        toast.error("Failed to update sections");
      } finally {
        setAddingSession(false);
      }
    } else {
      // Add default + saved custom sections that are checked, preserving reordered dialogSections order
      dialogSections.forEach((name) => {
        if (addSessionSections[name]) {
          sections.push({ id: uuidv4(), name, workouts: [] });
        }
      });

      // Add newly typed custom sections
      customSections.forEach((c) => {
        const trimmed = c.trim();
        if (
          trimmed &&
          !sections.some((s) => s.name.toLowerCase() === trimmed.toLowerCase())
        ) {
          sections.push({ id: uuidv4(), name: trimmed, workouts: [] });
        }
      });

      try {
        await api.post(
          `/member-portal/self-workouts/${selectedSelfWorkoutId}/sessions`,
          { date: selectedDateStr, sections },
          { headers: authHeaders() },
        );
        toast.success("Session added!");
        setAddSessionDialog(false);
        fetchSelfSessions(selectedSelfWorkoutId);
        fetchMemberSettings();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to add session");
      } finally {
        setAddingSession(false);
      }
    }
  };

  const handleUpdateSelfSession = async (sessionId, updatedSession) => {
    const originalSession = selfSessions.find((s) => s.id === sessionId);
    const originalSections = originalSession?.workoutSession?.sections || [];
    const originalCount = originalSections.reduce(
      (acc, sec) => acc + (sec.workouts?.length || 0),
      0,
    );

    const updatedSections = updatedSession?.sections || [];
    const updatedCount = updatedSections.reduce(
      (acc, sec) => acc + (sec.workouts?.length || 0),
      0,
    );

    if (originalCount > 0 && updatedCount === 0) {
      await handleDeleteSelfSession(sessionId);
      return;
    }

    const completedCount = updatedSections.reduce(
      (acc, sec) =>
        acc + (sec.workouts?.filter((w) => w._completed).length || 0),
      0,
    );
    const nextStatus =
      updatedCount > 0 && completedCount === updatedCount
        ? "completed"
        : "upcoming";

    setSelfSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId
          ? { ...s, status: nextStatus, workoutSession: updatedSession }
          : s,
      ),
    );
    try {
      await api.put(
        `/member-portal/self-workouts/${selectedSelfWorkoutId}/sessions/${sessionId}`,
        { workoutSession: updatedSession, status: nextStatus },
        { headers: authHeaders() },
      );
    } catch {
      toast.error("Failed to save changes");
    }
  };

  const handleDeleteSelfSession = async (sessionId) => {
    try {
      await api.delete(
        `/member-portal/self-workouts/${selectedSelfWorkoutId}/sessions/${sessionId}`,
        { headers: authHeaders() },
      );
      toast.success("Workout session deleted successfully");
      setSelfSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch {
      toast.error("Failed to delete session");
    }
  };

  const handleStartRepreparation = async () => {
    const token = localStorage.getItem("member_token");
    if (!token) return;
    setLoading(true);
    try {
      await api.post(
        "/member-portal/workouts/start-repreparation",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Re-preparation workouts scheduled successfully!");
      await fetchWorkouts();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to start re-preparation.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initData = async () => {
      const tokenVal = localStorage.getItem("member_token");
      if (!tokenVal) {
        navigate("/");
        return;
      }
      setLoading(true);
      try {
        // 1. Fetch settings to get lastUsedWorkoutCardId
        const settingsRes = await api.get("/member-portal/member-settings", {
          headers: { Authorization: `Bearer ${tokenVal}` },
        });
        const settings = settingsRes.data || {};
        setMemberSettings(settings);

        // 2. Fetch self-workout cards
        let selfCards = [];
        try {
          const selfRes = await api.get("/member-portal/self-workouts", {
            headers: { Authorization: `Bearer ${tokenVal}` },
          });
          selfCards = selfRes.data?.selfWorkouts || [];
          setSelfWorkoutCards(selfCards);
        } catch {
          // Ignore if 403 or disabled
        }

        // 3. Fetch trainer assignments list
        const workoutsRes = await api.get("/member-portal/workouts", {
          headers: { Authorization: `Bearer ${tokenVal}` },
        });
        const trainerCards = workoutsRes.data?.assignments || [];

        // 4. Resolve selected card ID
        const lastCardId = settings.lastUsedWorkoutCardId;
        let targetCardId = cardIdParam;

        if (!targetCardId && lastCardId) {
          const hasTrainerCard = trainerCards.some((a) => a.id === lastCardId);
          const hasSelfCard = selfCards.some((c) => c.id === lastCardId);
          if (hasTrainerCard || hasSelfCard) {
            targetCardId = lastCardId;
          }
        }

        if (!targetCardId && workoutsRes.data?.assignment) {
          targetCardId = workoutsRes.data.assignment.id;
        }

        if (!targetCardId && selfCards.length > 0) {
          targetCardId = selfCards[0].id;
        }

        // 5. Fetch full workout details for targetCardId
        if (targetCardId) {
          const isSelf = selfCards.some((c) => c.id === targetCardId);
          if (isSelf) {
            setSelectedSelfWorkoutId(targetCardId);
            setSelectedAssignmentId("");
            fetchSelfSessions(targetCardId);
            setData(workoutsRes.data);
          } else {
            const finalWorkoutsRes = await api.get("/member-portal/workouts", {
              params: { assignmentId: targetCardId },
              headers: { Authorization: `Bearer ${tokenVal}` },
            });
            setData(finalWorkoutsRes.data);
            setSelectedSelfWorkoutId("");
            setSelectedAssignmentId(targetCardId);
          }

          if (!cardIdParam) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("cardId", targetCardId);
                if (isSelf) {
                  next.set("tab", "calendar");
                }
                return next;
              },
              { replace: true },
            );
          }
        } else {
          setData(workoutsRes.data);
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/");
          return;
        }
        setError("Failed to load workouts.");
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, [navigate]);

  useEffect(() => {
    if (!scannerOpen) return;

    if (!window.isSecureContext) {
      toast.error(
        "Camera access requires a secure connection (HTTPS or localhost).",
      );
      setScannerOpen(false);
      return;
    }

    let html5QrCode;
    const timer = setTimeout(() => {
      const element = document.getElementById("workout-qr-reader");
      if (!element) return;

      html5QrCode = new Html5Qrcode("workout-qr-reader");

      const successCallback = async (decodedText) => {
        try {
          await html5QrCode.stop();
        } catch (e) {
          console.error("Failed to stop scanner:", e);
        }
        handleQrScanSuccess(decodedText);
      };

      const errorCallback = () => {};

      const qrboxConfig = (viewfinderWidth, viewfinderHeight) => {
        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
        const size = Math.floor(minEdge * 0.8);
        return { width: size, height: size };
      };

      const startWithFacingMode = () => {
        html5QrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: qrboxConfig },
            successCallback,
            errorCallback,
          )
          .catch((err) => {
            console.warn("Environment camera failed, trying user camera:", err);
            html5QrCode
              .start(
                { facingMode: "user" },
                { fps: 10, qrbox: qrboxConfig },
                successCallback,
                errorCallback,
              )
              .catch((err2) => {
                console.error("Both camera options failed:", err2);
                toast.error(
                  "Failed to access camera. Please check permissions.",
                );
                setScannerOpen(false);
              });
          });
      };

      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length > 0) {
            let cameraId = devices[0].id;
            const backCamera = devices.find(
              (device) =>
                device.label.toLowerCase().includes("back") ||
                device.label.toLowerCase().includes("rear") ||
                device.label.toLowerCase().includes("environment"),
            );
            if (backCamera) {
              cameraId = backCamera.id;
            }
            html5QrCode
              .start(
                cameraId,
                { fps: 10, qrbox: qrboxConfig },
                successCallback,
                errorCallback,
              )
              .catch((err) => {
                console.warn(
                  "Failed to start camera by ID, falling back to facingMode:",
                  err,
                );
                startWithFacingMode();
              });
          } else {
            startWithFacingMode();
          }
        })
        .catch((err) => {
          console.warn(
            "getCameras failed, trying facingMode direct start:",
            err,
          );
          startWithFacingMode();
        });
    }, 500);

    return () => {
      clearTimeout(timer);
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode
          .stop()
          .then(() => {
            html5QrCode.clear();
          })
          .catch((err) => console.error("Scanner cleanup error:", err));
      }
    };
  }, [scannerOpen]);

  const handleQrScanSuccess = async (decodedText) => {
    setScannerOpen(false);

    let orgId = "";
    let branchId = "";

    const urlMatch = decodedText.match(
      /\/public\/attendance\/([^/]+)\/([^/]+)/,
    );
    if (urlMatch) {
      orgId = urlMatch[1];
      branchId = urlMatch[2];
    } else {
      const parts = decodedText.split(":");
      if (parts[0] === "ATTENDANCE" && parts.length >= 3) {
        orgId = parts[1];
        branchId = parts[2];
      }
    }

    if (!orgId || !branchId) {
      toast.error("Invalid QR Code. Please scan a valid gym check-in QR code.");
      return;
    }

    const member = JSON.parse(localStorage.getItem("member_data") || "{}");
    if (!member?.phone) {
      toast.error("Member details not found. Please login again.");
      return;
    }

    toast.loading("Processing check-in...");
    try {
      const res = await api.post("/attendance/public-checkin", {
        phone: member.phone,
        organizationId: orgId,
        branchId: branchId,
      });

      toast.dismiss();
      toast.success(res.data?.message || "Check-in successful!");
      setLoading(true);
      fetchWorkouts();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || "Check-in failed.");
    }
  };

  // ── Derived values (must be above ALL early returns to satisfy Rules of Hooks) ──
  const stats = data?.stats;
  const schedule = data?.schedule || [];
  const weekPlan = data?.weeklyPlan || {};
  const trainer = data?.trainer;
  const noAssignment = !data?.assignment && !selectedSelfWorkoutId;
  const selectedSelfCard =
    selfWorkoutCards.find((c) => c.id === selectedSelfWorkoutId) || null;
  const isSelfWorkoutMode = !!selectedSelfCard;

  const allDropdownAssignments = useMemo(() => {
    const trainerCards = data?.assignments || [];
    const selfCards = selfWorkoutCards.map((c) => ({ ...c, _isSelf: true }));
    return [...trainerCards, ...selfCards];
  }, [data?.assignments, selfWorkoutCards]);

  const selfSessionForDate = useMemo(() => {
    if (!isSelfWorkoutMode) return null;
    return selfSessions.find((s) => s.date === selectedDateStr) || null;
  }, [isSelfWorkoutMode, selfSessions, selectedDateStr]);

  const selfSessionsForDate = useMemo(() => {
    if (!isSelfWorkoutMode) return [];
    return selfSessions.filter((s) => s.date === selectedDateStr);
  }, [isSelfWorkoutMode, selfSessions, selectedDateStr]);

  const selfSessionDates = useMemo(() => {
    return selfSessions.map((s) => ({
      date: s.date,
      status: s.status || "upcoming",
    }));
  }, [selfSessions]);

  const TABS = [
    { key: "calendar", label: "Calendar", icon: CalendarDays },
    isSelfWorkoutMode
      ? { key: "all-workouts", label: "All Workouts", icon: ClipboardList }
      : { key: "schedules", label: "All Schedules", icon: TrendingUp },
  ];

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p>{error}</p>
      </div>
    );

  const forceCheckin = data?.forceCheckinToSeeWorkouts;
  const isCheckedIn = data?.isCheckedInToday;
  const membershipBlocked = data?.membershipBlocked;

  if (membershipBlocked) {
    return (
      <div
        className="flex flex-col gap-4 pb-8"
        data-testid="workouts-blocked-view"
      >
        <div>
          <h1 className="text-xl font-black font-heading uppercase tracking-wide">
            Workouts
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Your assigned workout plan
          </p>
        </div>

        <Card className="bg-card border-border rounded-sm max-w-md mx-auto mt-8">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4 text-red-500">
              <AlertCircle className="h-8 w-8" />
            </div>
            <CardTitle className="font-heading text-xl">
              Membership Required
            </CardTitle>
            <CardDescription className="text-sm">
              You must have an active membership to view workouts. Please
              contact your gym administrator or renew your membership.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (forceCheckin && !isCheckedIn) {
    return (
      <div
        className="flex flex-col gap-4 pb-8"
        data-testid="workouts-locked-view"
      >
        <div>
          <h1 className="text-xl font-black font-heading uppercase tracking-wide">
            Workouts
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Your assigned workout plan
          </p>
        </div>

        <Card className="bg-card border-border rounded-sm max-w-md mx-auto mt-8">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 text-primary">
              <QrCode className="h-8 w-8 animate-pulse" />
            </div>
            <CardTitle className="font-heading text-xl">
              Check-In Required
            </CardTitle>
            <CardDescription className="text-sm">
              Please check-in to view today's workouts.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <button
              onClick={() => setScannerOpen(true)}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
              data-testid="workout-scan-btn"
            >
              <QrCode className="h-4 w-4" />
              Scan QR Code to Check In
            </button>
          </CardContent>
        </Card>

        {/* QR Scanner Dialog */}
        <Dialog open={scannerOpen} onOpenChange={setScannerOpen}>
          <DialogContent className="max-w-md w-[90%] rounded-lg border border-border bg-card p-6 shadow-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-bold">
                Scan Gym QR Code
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-1">
                Position the gym entrance QR code within the scanner viewport to
                check in.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center mt-4">
              <div
                id="workout-qr-reader"
                className="w-full aspect-square overflow-hidden rounded-md bg-black relative border border-border"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // (noAssignment, isSelfWorkoutMode, allDropdownAssignments, selfSessionForDate, selfSessionDates, TABS
  // are defined before the early returns above — see top of render function)

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black font-heading uppercase tracking-wide">
            Workouts
          </h1>
          <p className="text-muted-foreground text-xs mt-0.5">
            {isSelfWorkoutMode
              ? "Self-created workout"
              : "Your assigned workout plan"}
          </p>
        </div>
        <div className="flex flex-col gap-2 min-w-[240px]">
          {allDropdownAssignments.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground truncate">
                Selected Workout Card
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <Select
                  value={selectedSelfWorkoutId || selectedAssignmentId}
                  onValueChange={handleAssignmentChange}
                >
                  <SelectTrigger className="w-full h-9 bg-card font-heading text-sm font-semibold select-none uppercase tracking-wide">
                    <SelectValue placeholder="Select workout card" />
                  </SelectTrigger>
                  <SelectContent>
                    {(data?.assignments || []).map((a) => (
                      <SelectItem
                        key={a.id}
                        value={a.id}
                        className="uppercase font-heading text-sm"
                      >
                        {a.cardName || "Unnamed Card"} —{" "}
                        <span className="text-[9px] text-muted-foreground">
                          {a.status}
                        </span>
                      </SelectItem>
                    ))}
                    {selfWorkoutCards.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-secondary/30 border-t border-border/40 mt-1 flex items-center gap-1">
                          <Zap className="h-3 w-3 text-amber-500" /> Self
                          Workouts
                        </div>
                        {selfWorkoutCards.map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            className="font-heading tracking-wider  text-sm"
                          >
                            ⚡ {c.cardName}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {/* {isSelfWorkoutMode && (
                  <Badge className="border text-[10px] font-heading tracking-wide uppercase px-2.5 py-1.5 select-none shrink-0 bg-amber-500/15 text-amber-500 border-amber-500/20">
                    Self
                  </Badge>
                )}
                {!isSelfWorkoutMode && data?.assignment && (
                  <Badge
                    className={cn(
                      "border text-[10px] font-heading tracking-wide uppercase px-2.5 py-1.5 select-none shrink-0",
                      data.assignment.status === "active"
                        ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/20"
                        : data.assignment.status === "paused"
                          ? "bg-amber-500/15 text-amber-500 border-amber-500/20"
                          : "bg-muted text-muted-foreground border-border",
                    )}
                  >
                    {data.assignment.status}
                  </Badge>
                )} */}
              </div>
            </div>
          )}
          {/* Create self-workout card button */}
          {memberSettings.allowMemberSelfWorkout && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateCardDialog(true)}
              className="h-8 text-xs font-heading uppercase tracking-wide border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 self-start"
            >
              <Plus className="h-3 w-3 mr-1.5" />
              Create My Workout Card
            </Button>
          )}
        </div>
      </div>

      {/* Grace Period Alert */}
      {data?.inGracePeriod && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 text-amber-600 dark:text-amber-500 text-xs md:text-sm shadow-sm shrink-0">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-500 mt-0.5" />
          <div className="flex-1 space-y-1.5">
            <div className="font-bold text-sm tracking-tight text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <span>Grace Period Active</span>
            </div>
            <div className="space-y-0.5 text-muted-foreground text-xs leading-normal">
              <p>
                Your membership expired on{" "}
                <span className="font-semibold text-foreground">
                  {formatGraceDate(data.membershipExpiryDate)}
                </span>
                .
              </p>
              <p>
                You can continue using gym services until{" "}
                <span className="font-semibold text-foreground">
                  {formatGraceDate(data.gracePeriodEndDate)}
                </span>
                .
              </p>
            </div>
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-500 pt-0.5">
              Renew now to avoid access interruption.
            </p>
          </div>
        </div>
      )}

      {/* Create card dialog */}
      <Dialog open={createCardDialog} onOpenChange={setCreateCardDialog}>
        <DialogContent className="bg-card border-border max-w-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold font-heading uppercase tracking-wide">
              <Zap className="h-4 w-4 text-amber-500" /> Create My Workout Card
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Give your self-workout a name. You can add exercises each day.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1">
            <Label className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground ">
              Card Name *
            </Label>
            <Input
              value={newCardName}
              onChange={(e) => setNewCardName(e.target.value)}
              placeholder="e.g. My Morning Routine"
              onKeyDown={(e) => e.key === "Enter" && handleCreateCard()}
              className="h-9 text-sm"
            />
          </div>
          <DialogFooter className="flex-row gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateCardDialog(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateCard}
              disabled={!newCardName.trim() || creatingCard}
            >
              {creatingCard ? "Creating…" : "Create Card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Session dialog */}
      <Dialog
        open={addSessionDialog}
        onOpenChange={(open) => {
          setAddSessionDialog(open);
          if (!open) {
            setEditingSessionForSections(null);
            setCustomSections([]);
          }
        }}
      >
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader className="text-left">
            <DialogTitle className="font-heading uppercase tracking-wide text-sm">
              Add Workout Session
            </DialogTitle>
            <DialogDescription className="text-xs">
              For {selectedDateStr} — select which sections to include.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {dialogSections.map((name, idx) => (
              <div
                key={name}
                className="flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`sec-${name}`}
                    checked={!!addSessionSections[name]}
                    onCheckedChange={(v) =>
                      setAddSessionSections((p) => ({ ...p, [name]: !!v }))
                    }
                  />
                  <Label
                    htmlFor={`sec-${name}`}
                    className="text-sm font-medium capitalize cursor-pointer"
                  >
                    {name}
                  </Label>
                </div>

                {/* Move up/down buttons */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={idx === 0}
                    onClick={() => {
                      const newSections = [...dialogSections];
                      const temp = newSections[idx];
                      newSections[idx] = newSections[idx - 1];
                      newSections[idx - 1] = temp;
                      setDialogSections(newSections);
                    }}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-secondary/50 disabled:opacity-25"
                    title="Move Up"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={idx === dialogSections.length - 1}
                    onClick={() => {
                      const newSections = [...dialogSections];
                      const temp = newSections[idx];
                      newSections[idx] = newSections[idx + 1];
                      newSections[idx + 1] = temp;
                      setDialogSections(newSections);
                    }}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-secondary/50 disabled:opacity-25"
                    title="Move Down"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Custom sections */}
            {customSections.map((sec, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={sec}
                  placeholder={`Custom Section ${idx + 1}`}
                  onChange={(e) => {
                    const newCustoms = [...customSections];
                    newCustoms[idx] = e.target.value;
                    setCustomSections(newCustoms);
                  }}
                  className="text-sm h-9 flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={() => {
                    setCustomSections(
                      customSections.filter((_, i) => i !== idx),
                    );
                  }}
                  className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {/* Button to add custom section */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCustomSections([...customSections, ""])}
              className="text-xs w-full font-heading uppercase tracking-wide h-8 border-dashed flex items-center justify-center gap-1.5 mt-2"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Custom Section
            </Button>
          </div>
          <DialogFooter className="flex-row gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAddSessionDialog(false);
                setEditingSessionForSections(null);
                setCustomSections([]);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAddSession}
              disabled={
                addingSession ||
                (!Object.values(addSessionSections).some(Boolean) &&
                  !customSections.some((s) => s.trim()))
              }
            >
              {addingSession ? "Adding…" : "Add Session"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {noAssignment && !isSelfWorkoutMode ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
            <Dumbbell className="h-10 w-10 opacity-30" />
            <p className="text-sm">No workout plan assigned yet</p>
            <p className="text-xs text-center max-w-xs">
              Contact the gym staff to get a personalised workout plan.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {isRePrepLocked && (
            <Card className="border-red-500 bg-red-500/5 dark:bg-red-950/10 shadow-lg border">
              <CardContent className="p-5 flex flex-col lg:flex-row items-stretch gap-6">
                {/* Left side: warning icon and message */}
                <div className="flex-1 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 dark:bg-red-500/20 flex items-center justify-center text-red-500 shrink-0">
                    <AlertCircle className="h-6 w-6" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-heading font-black text-red-600 dark:text-red-400 uppercase tracking-wider text-base animate-pulse">
                      Workout Lock Active
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You have missed{" "}
                      <strong>{missedCount} workout days</strong>. Since you
                      reached your limit of{" "}
                      <strong>{fitnessRePreparationDays} days</strong>, upcoming
                      workouts are locked.
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You can self-start a <strong>re-preparation phase</strong>{" "}
                      to get back on track safely, or contact your trainer to
                      adjust your program schedule.
                    </p>
                  </div>
                </div>

                {/* Vertical Divider */}
                <div className="hidden lg:block w-[1px] bg-border/40" />

                {/* Right side: actions and contact details */}
                <div className="flex flex-col justify-between gap-4 min-w-[260px]">
                  <div className="space-y-2.5">
                    <button
                      onClick={handleStartRepreparation}
                      disabled={loading}
                      className="w-full py-2.5 px-4 rounded-xl bg-red-500 text-white hover:bg-red-600 text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                    >
                      <Play className="h-4 w-4" />
                      {loading
                        ? "Scheduling..."
                        : "Start Re-preparation Workouts"}
                    </button>
                  </div>

                  {trainer && (
                    <div className="p-3 rounded-xl bg-card border border-border/60 space-y-1.5 shadow-sm">
                      <p className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground font-bold">
                        Your Trainer Contacts
                      </p>
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-foreground leading-none">
                          {trainer.name}
                        </p>
                        {trainer.email && (
                          <p className="text-[10px] text-muted-foreground leading-none mt-0.5">
                            {trainer.email}
                          </p>
                        )}
                        {trainer.phone && (
                          <p className="text-[10px] text-muted-foreground leading-none mt-0.5">
                            {trainer.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Plan Metadata & Stats Overview — hidden for self-workout cards */}
          {!isSelfWorkoutMode && (
            <div className="grid grid-cols-1 md:grid-cols-3">
              {/* Stats inside a single compact Card */}
              <Card className="md:col-span-2 bg-card border-border shadow-sm">
                <CardContent className="px-3 py-2 sm:p-4 flex flex-col justify-between h-full gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold font-mono uppercase tracking-wider text-muted-foreground">
                          Progress Summary
                        </p>
                        {trainer && (
                          <p className="text-[10.5px] text-muted-foreground flex items-center gap-1 font-mono">
                            <span>👤 Coach:</span>
                            <span className="font-bold text-foreground">
                              {trainer.name}
                            </span>
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-xs font-black text-primary font-mono">
                          {stats?.total > 0
                            ? Math.round(
                                ((stats?.completed ?? 0) / stats.total) * 100,
                              )
                            : 0}
                          %
                        </span>
                        <span className="text-[10px] text-muted-foreground font-mono mt-0.5">
                          {stats?.completed ?? 0} / {stats?.total ?? 0} Day
                          {stats?.total !== 1 ? "s" : ""} Completed
                        </span>
                      </div>
                    </div>

                    {/* Visual Progress Bar */}
                    <div className="w-full h-2.5 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-emerald-500 rounded-full transition-all duration-500 ease-out"
                        style={{
                          width: `${
                            stats?.total > 0
                              ? Math.round(
                                  ((stats?.completed ?? 0) / stats.total) * 100,
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-secondary/30 rounded-xl p-2 text-center flex flex-col items-center justify-center">
                      <span className="text-xs font-bold text-muted-foreground uppercase font-mono tracking-tight flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        Completed
                      </span>
                      <span className="text-base font-black text-green-500 mt-1">
                        {stats?.completed ?? 0}
                      </span>
                    </div>

                    <div className="bg-secondary/30 rounded-xl p-2 text-center flex flex-col items-center justify-center">
                      <span className="text-xs font-bold text-muted-foreground uppercase font-mono tracking-tight flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                        Upcoming
                      </span>
                      <span className="text-base font-black text-amber-400 mt-1">
                        {stats?.remaining ?? 0}
                      </span>
                    </div>

                    <div className="bg-secondary/30 rounded-xl p-2 text-center flex flex-col items-center justify-center">
                      <span className="text-xs font-bold text-muted-foreground uppercase font-mono tracking-tight flex items-center gap-1">
                        <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        Missed
                      </span>
                      <span className="text-base font-black text-red-500 mt-1">
                        {stats?.missed ?? 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Weekly pattern column */}
              <div className="flex flex-col gap-2.5">
                <WeeklyPattern weekPlan={weekPlan} />
              </div>
            </div>
          )}

          {/* Self-workout stats — same card structure as trainer stats */}
          {isSelfWorkoutMode &&
            (() => {
              const completedSessions = selfSessions.filter(
                (s) => s.status === "completed",
              ).length;
              const totalExercises = selfSessions.reduce(
                (acc, s) =>
                  acc +
                  (s.workoutSession?.sections || []).reduce(
                    (a, sec) => a + (sec.workouts?.length || 0),
                    0,
                  ),
                0,
              );
              const completedExercises = selfSessions.reduce(
                (acc, s) =>
                  acc +
                  (s.workoutSession?.sections || []).reduce(
                    (a, sec) =>
                      a +
                      (sec.workouts || []).filter(
                        (w) => w._completed || w.completed,
                      ).length,
                    0,
                  ),
                0,
              );
              return (
                <Card className="bg-card border-border shadow-sm">
                  <CardContent className="p-3 flex flex-col justify-between h-full gap-3">
                    <p className="text-xs font-semibold font-mono uppercase tracking-wider text-muted-foreground">
                      Workout Activity Stats
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-secondary/30 rounded-xl p-2 text-center flex flex-col items-center justify-center">
                        <span className="text-xs font-bold text-muted-foreground uppercase font-mono tracking-tighter flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          Workouts
                        </span>
                        <span className="text-base font-black text-green-500 mt-1">
                          {completedExercises}
                        </span>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-2 text-center flex flex-col items-center justify-center">
                        <span className="text-xs font-bold text-muted-foreground uppercase font-mono tracking-tighter flex items-center gap-1">
                          <Dumbbell className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          Exercises
                        </span>
                        <span className="text-base font-black text-foreground mt-1">
                          {totalExercises}
                        </span>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-2 text-center flex flex-col items-center justify-center">
                        <span className="text-xs font-bold text-muted-foreground uppercase font-mono tracking-tighter flex items-center gap-1">
                          <CalendarIcon className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                          Days
                        </span>
                        <span className="text-base font-black text-blue-500 mt-1">
                          {completedSessions}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

          {/* Tab switcher — equal halves, border-r divider between */}
          <div className="flex w-full rounded-xl overflow-hidden border border-border">
            {TABS.map(({ key, label, icon: Icon }, i) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={[
                  "flex-1 flex items-center justify-center gap-2 py-2.5 text-xs tracking-wider uppercase font-semibold transition-colors",
                  i < TABS.length - 1 ? "border-r border-border" : "",
                  tab === key
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "calendar" && (
            <div className="flex flex-col gap-4">
              <WorkoutCalendar
                schedule={
                  isSelfWorkoutMode ? selfSessionDates : filteredSchedule
                }
                currentMonth={currentMonth}
                onMonthChange={handleMonthChange}
                selectedDateStr={
                  isSelfWorkoutMode ? selectedDateStr : activeSelectedDateStr
                }
                onSelectDate={handleDateChange}
                isRePrepLocked={isSelfWorkoutMode ? false : isRePrepLocked}
                todayStr={todayStr}
                isSelfWorkoutMode={isSelfWorkoutMode}
              />
              {/* Inline workout details panel */}
              <div ref={workoutDetailRef} className="scroll-mt-4">
                {isSelfWorkoutMode ? (
                  selfSessionsLoading ? (
                    <Card className="bg-card border-border">
                      <CardContent className="py-8 text-center text-muted-foreground">
                        <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin opacity-40" />
                        <p className="text-sm">Loading sessions…</p>
                      </CardContent>
                    </Card>
                  ) : selfSessionForDate ? (
                    <div className="space-y-4">
                      <Card className="border-0 shadow-sm">
                        <WorkoutSessionView
                          session={selfSessionForDate}
                          allowEdit={true}
                          allowComplete={true}
                          enableTimer={false}
                          isStaff={false}
                          isMemberWorkout={true}
                          onSessionUpdate={(updatedSession) =>
                            handleUpdateSelfSession(
                              selfSessionForDate.id,
                              updatedSession,
                            )
                          }
                          onDeleteSession={() =>
                            handleDeleteSelfSession(selfSessionForDate.id)
                          }
                        />
                      </Card>

                      <Button
                        onClick={() => {
                          setEditingSessionForSections(selfSessionForDate);
                          setAddSessionDialog(true);
                        }}
                        className="w-full h-10 border-dashed text-xs font-heading uppercase tracking-wide flex items-center justify-center gap-1.5"
                        variant="outline"
                      >
                        <Settings className="h-4 w-4" />
                        Manage Sections
                      </Button>
                    </div>
                  ) : (
                    <Card className="bg-card border-border shadow-sm">
                      <CardContent className="py-8 text-center text-muted-foreground flex flex-col items-center gap-3">
                        <CalendarIcon className="h-8 w-8 opacity-30" />
                        <p className="text-sm font-medium">
                          No session for this day
                        </p>
                        <p className="text-xs">
                          {parseLocalDate(selectedDateStr)?.toLocaleDateString(
                            "en-IN",
                            {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                            },
                          ) || selectedDateStr}
                        </p>
                        <Button
                          size="sm"
                          onClick={() => setAddSessionDialog(true)}
                          className="h-8 text-xs font-heading uppercase tracking-wide"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1.5" />
                          Add Session for This Day
                        </Button>
                      </CardContent>
                    </Card>
                  )
                ) : (
                  (() => {
                    const isSelectedDayLocked =
                      !data?.assignment?.isFlexible &&
                      allowMemberToComplete &&
                      hasUnresolvedMissed &&
                      selectedSession &&
                      selectedSession.date >= todayStr &&
                      selectedSession.status === "upcoming";

                    return selectedSession ? (
                      <DayDetailInline
                        key={selectedSession.date}
                        session={selectedSession}
                        allowMemberToComplete={
                          data?.assignment?.allowMemberToComplete
                        }
                        enableTimer={data?.assignment?.enableTimer}
                        onSuccess={fetchWorkouts}
                        isLocked={isSelectedDayLocked}
                        isFlexible={data?.assignment?.isFlexible}
                        workoutCard={data?.assignment?.workoutCard}
                        missedWorkoutPolicy={missedWorkoutPolicy}
                        missedCount={unresolvedMissedCount}
                        fitnessPreparationDays={
                          data?.assignment?.fitnessPreparationDays
                        }
                        fitnessRePreparationDays={
                          data?.assignment?.fitnessRePreparationDays
                        }
                      />
                    ) : (
                      <Card className="bg-card border-border shadow-sm">
                        <CardContent className="py-8 text-center text-muted-foreground">
                          <Dumbbell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p className="text-sm font-medium">Rest Day</p>
                          <p className="text-xs">
                            No workouts scheduled for{" "}
                            {parseLocalDate(
                              activeSelectedDateStr,
                            )?.toLocaleDateString("en-IN", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                            }) || activeSelectedDateStr}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })()
                )}
              </div>
            </div>
          )}
          {tab === "schedules" && !isSelfWorkoutMode && (
            <AllSchedules
              schedule={filteredSchedule}
              onSelectSession={(s) => handleDateChange(s.date)}
              selectedDateStr={activeSelectedDateStr}
              allowMemberToComplete={data?.assignment?.allowMemberToComplete}
              enableTimer={data?.assignment?.enableTimer}
              onSuccess={fetchWorkouts}
              hasUnresolvedMissed={hasUnresolvedMissed}
              todayStr={todayStr}
              isFlexible={data?.assignment?.isFlexible}
              workoutCard={data?.assignment?.workoutCard}
              missedWorkoutPolicy={missedWorkoutPolicy}
              missedCount={unresolvedMissedCount}
              fitnessPreparationDays={data?.assignment?.fitnessPreparationDays}
              fitnessRePreparationDays={
                data?.assignment?.fitnessRePreparationDays
              }
            />
          )}
          {tab === "all-workouts" && isSelfWorkoutMode && (
            <div className="space-y-2">
              {selfSessionsLoading ? (
                <Card className="bg-card border-border">
                  <CardContent className="py-8 text-center">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin opacity-40" />
                  </CardContent>
                </Card>
              ) : selfSessions.length === 0 ? (
                <Card className="bg-card border-border">
                  <CardContent className="py-10 text-center text-muted-foreground flex flex-col items-center gap-3">
                    <ClipboardList className="h-8 w-8 opacity-30" />
                    <p className="text-sm">
                      No sessions yet. Go to Calendar and add your first
                      session!
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Accordion type="multiple">
                  {selfSessions.map((session) => {
                    const d = parseLocalDate(session.date);
                    const dayLabel =
                      d?.toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      }) || session.date;
                    const secs = session.workoutSession?.sections || [];
                    const totalEx = secs.reduce(
                      (acc, s) => acc + (s.workouts?.length || 0),
                      0,
                    );
                    return (
                      <AccordionItem
                        key={session.id}
                        value={session.id}
                        className="border border-border/60 rounded-xl overflow-hidden mb-2"
                      >
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/20">
                          <div className="flex items-center justify-between w-full mr-2">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-primary shrink-0" />
                              <span className="text-sm font-bold font-heading">
                                {dayLabel}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {totalEx} exercise{totalEx !== 1 ? "s" : ""}
                              </span>
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[9px] px-1.5 py-0 border",
                                  session.status === "completed"
                                    ? "text-emerald-500 border-emerald-500/30 bg-emerald-500/5"
                                    : "text-amber-500 border-amber-500/30 bg-amber-500/5",
                                )}
                              >
                                {session.status === "completed"
                                  ? "Done"
                                  : "Active"}
                              </Badge>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 pt-2">
                          <WorkoutSessionView
                            session={session}
                            allowEdit={true}
                            allowComplete={true}
                            enableTimer={false}
                            isStaff={false}
                            isMemberWorkout={true}
                            onSessionUpdate={(updatedSession) =>
                              handleUpdateSelfSession(
                                session.id,
                                updatedSession,
                              )
                            }
                            onDeleteSession={() =>
                              handleDeleteSelfSession(session.id)
                            }
                          />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
